import { useMutation, useQuery } from '@apollo/client/react/hooks';
import AddHollow from '@clearkit/icons/glyphs/AddHollow';
import Unknown from '@clearkit/icons/glyphs/Unknown';
import Spinner from '@clearkit/icons/indicators/Spinner';
import { CKBox, CKButton, CKModal, CKSwitch, CKTooltip } from '@clearkit/react';
import CircularProgress from '@material-ui/core/CircularProgress';
import capitalize from 'lodash/capitalize';
import groupBy from 'lodash/groupBy';
import { useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import Footer from '~/components/Footer';
import LoadingArea from '~/components/LoadingArea';
import ToastsContainer from '~/containers/Toasts';
import { readableType } from '~/lib/dataEnrichment';

import cancel from './cancel.graphql';
import EmptyState from './EmptyState.svg';
import FieldMappingGroup from './FieldMappingGroup';
import FieldMappingRow from './FieldMappingRow';
import mutation from './mutation.graphql';
import query from './query.graphql';
import toggle from './toggle.graphql';
import trigger from './trigger.graphql';

const STUB_MAPPING = {
  targetField: null,
  sourceField: null,
  sourceType: null,
  sourceObjectType: null,
  /* eslint-disable */
  // joaomdmoura: DISCLAIMER
  // We ended up changing the way this feature works after we implemented it,
  // and that led to some weird naming issue
  //
  // Despite the name being conditionaOverwrite, in the interface we indicate the
  // oposite value of this (if it would hard overwrite data or not)
  // hardOverwrite = !conditionalOverwrite
  //
  // By default the conditonalOverwrite should be set to true, as data is not
  // overwritten by default, and that will show the hardOverwrite Switch selector
  // as disabled
  /* eslint-enable */
  conditionalOverwrite: true, // hardOverwrite = false
  dataType: null,
  createdAt: null,
};

function clearbitTypesForMapping(type) {
  switch (type) {
    case 'account':
    case 'company':
      return ['clearbit_company'];
    default:
      return ['clearbit_company', 'clearbit_person'];
  }
}

function typeServices(type) {
  switch (type) {
    case 'salesforce':
      return ['salesforce', 'salesforcesandbox'];
    default:
      return [type];
  }
}

function StartBackfillButton({ disabled, isLoading, onClick }) {
  if (disabled) {
    return (
      <CKTooltip
        tooltip={<p className="m-2">Enrichment needs to be enabled</p>}
        placement="top">
        <div>
          <CKButton disabled isLoading={isLoading}>
            Start Backfill
          </CKButton>
        </div>
      </CKTooltip>
    );
  }

  return (
    <CKButton isLoading={isLoading} onClick={onClick}>
      Start Backfill
    </CKButton>
  );
}

const FieldMappingForm = ({ integration, objectType }) => {
  const [fieldMappings, setFieldMappings] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [confirmToggleDialogOpen, setConfirmToggleDialogOpen] = useState(false);
  const [confirmSaveDialogOpen, setConfirmSaveDialogOpen] = useState(false);
  const [showInvalidFields, setShowInvalidFields] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const {
    data,
    data: { accountConnector = {}, sourceAttributes, targetAttributes } = {},
    loading,
    refetch,
  } = useQuery(query, {
    fetchPolicy: 'no-cache',
    variables: {
      attributeType: capitalize(objectType.type),
      refreshAttributes: false,
      service: typeServices(integration),
      sourceTypes: [
        ...clearbitTypesForMapping(objectType.name),
        `custom_traits_${objectType.type}`,
        `reveal_${objectType.type}`,
        `segment_${objectType.type}`,
        `system_traits_${objectType.type}`,
      ],
      subCategory: objectType.name,
      targetObjectType: capitalize(objectType.name),
      targetTypes: [`${integration}_${objectType.name}`],
    },
  });

  const onClickRefreshAttributes = () => {
    return refetch({ refreshAttributes: true });
  };

  const savedFieldMapping = accountConnector?.fieldMapping;

  const input = useMemo(
    () => ({
      id: accountConnector?.id,
      targetObjectType: capitalize(objectType.name),
      fieldMappings,
    }),
    [accountConnector, fieldMappings, objectType],
  );

  const [updateFieldMapping, { loading: isUpdating }] = useMutation(mutation, {
    onCompleted: () => {
      ToastsContainer.addSuccess(
        `${readableType(integration)} ${capitalize(
          objectType.name,
        )} field mapping saved!`,
      );
    },
    onError: () => {
      ToastsContainer.addError('There was an error saving this field mapping.');
    },
    variables: {
      input,
    },
  });

  const [toggleFieldMapping, { loading: isToggling }] = useMutation(toggle, {
    onCompleted: () => {
      ToastsContainer.addSuccess(
        `${readableType(integration)} ${capitalize(
          objectType.name,
        )} field mapping ${isActive ? 'disabled' : 'enabled'}!`,
      );
    },
    onError: () => {
      ToastsContainer.addError(
        `There was an error ${
          isActive ? 'disabling' : 'enabling'
        } this field mapping.`,
      );
    },
    refetchQueries: ['FieldMappingForm'],
    variables: {
      input: {
        id: accountConnector?.id,
        targetObjectType: capitalize(objectType.name),
        enabled: !isActive,
      },
    },
  });

  const [triggerBackfill, { loading: triggering }] = useMutation(trigger, {
    onCompleted: () => {
      ToastsContainer.addSuccess(
        `${readableType(integration)} ${capitalize(
          objectType.name,
        )} backfill started!`,
      );
    },
    onError: () => {
      ToastsContainer.addError(`There was an error starting this backfill.`);
    },
    refetchQueries: ['FieldMappingForm'],
    variables: {
      input: {
        id: accountConnector?.id,
        targetObjectType: objectType.name,
      },
    },
  });

  const [cancelBackfill, { loading: cancelling }] = useMutation(cancel, {
    onCompleted: () => {
      ToastsContainer.addSuccess(
        `${capitalize(integration)} ${capitalize(
          objectType.name,
        )} backfill cancelled!`,
      );
    },
    onError: () => {
      ToastsContainer.addError(`There was an error cancelling this backfill.`);
    },
    refetchQueries: ['FieldMappingForm'],
    variables: {
      input: {
        id: accountConnector?.id,
        targetObjectType: objectType.name,
      },
    },
  });

  useEffect(() => {
    (async () => {
      await refetch();
    })();
  }, [objectType]);

  const accountConnectorBackfillActive =
    accountConnector?.backfillActive || false;
  const backfillLoading = triggering || cancelling;

  const handleMappingChange = (index) => (changes) => {
    const newFieldMappings = [...fieldMappings];
    newFieldMappings[index] = {
      ...newFieldMappings[index],
      ...changes,
    };
    setFieldMappings(newFieldMappings);
  };

  const applyChangesToAllGroupMembers = (groupId) => (changes) => {
    const newFieldMappings = [...fieldMappings];
    const updatedMappings = newFieldMappings.reduce((result, mapping) => {
      if (mapping.groupId === groupId) {
        result.push({
          ...mapping,
          ...changes,
        });
      } else {
        result.push(mapping);
      }

      return result;
    }, []);
    setFieldMappings(updatedMappings);
  };

  const addMapping = (attributes = {}) => {
    attributes.createdAt = new Date().toISOString();

    setFieldMappings([...fieldMappings, { ...STUB_MAPPING, ...attributes }]);
  };

  const removeMapping = (indexToRemove) => () => {
    setFieldMappings(fieldMappings.filter((_, i) => i !== indexToRemove));
  };

  const removeMappingGroup = (groupId) => () => {
    setFieldMappings(
      fieldMappings.filter((mapping) => mapping.groupId !== groupId),
    );
  };

  const updateErrors = (key, value) => {
    const errors = fieldErrors;
    if (value === undefined) {
      delete errors[key];
      setFieldErrors(errors);
    } else if (errors[key] === undefined) {
      errors[key] = [value];
      setFieldErrors(errors);
    } else {
      errors[key].push(value);
      setFieldErrors(errors);
    }
  };

  const findDuplicates = (array) => {
    return array.filter((value, index, arry) => {
      if (arry.indexOf(value) !== index) return value;
    }, []);
  };

  const areFieldMappingsValid = useMemo(() => {
    const nullFields = fieldMappings
      .map((fm) => [fm.targetField, fm.sourceField])
      .flat()
      .includes(null);

    const targetMappings = fieldMappings.map((fm) => fm.targetField);
    const targetFieldDuplicates = findDuplicates(targetMappings);
    if (targetFieldDuplicates.length > 0) {
      targetFieldDuplicates.forEach((targetField) => {
        updateErrors(targetField);
        updateErrors(targetField, 'duplicate field');
      });
    } else {
      setFieldErrors({});
    }

    return nullFields || targetFieldDuplicates.length > 0;
  }, [fieldMappings]);

  useEffect(() => {
    if (savedFieldMapping?.fieldMappings) {
      setFieldMappings([...savedFieldMapping?.fieldMappings]);
    }
  }, [objectType.name, savedFieldMapping?.fieldMappings]);

  useEffect(() => {
    if (savedFieldMapping) {
      setIsActive(savedFieldMapping.enabled);
    }
  }, [savedFieldMapping?.enabled]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <CircularProgress />
      </div>
    );
  }

  const indexedMappings = fieldMappings.map((mapping, index) => {
    return { ...mapping, index };
  });

  const groupedMappings = groupBy(
    indexedMappings,
    (mapping) => mapping.groupId,
  );

  const sortedMappings = Object.keys(groupedMappings)
    .reduce((result, key) => {
      if (key === 'undefined') {
        return result.concat(groupedMappings[key]);
      }

      result.push({
        groupId: key,
        conditionalOverwrite: groupedMappings[key][0].conditionalOverwrite,
        createdAt: groupedMappings[key][0].createdAt,
        fieldMappings: groupedMappings[key],
      });
      return result;
    }, [])
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const SaveButton = () => {
    return (
      <CKButton
        isLoading={isUpdating}
        onClick={() => setConfirmSaveDialogOpen(true)}
        variant={CKButton.Variant.bold}
        isDisabled={areFieldMappingsValid}
        type="submit"
        variantColor={CKButton.VariantColor.green}>
        Save changes
      </CKButton>
    );
  };

  return (
    <div
      key={`field-mapping-${objectType.name}`}
      className="flex flex-col justify-between h-full bg-gray-0">
      <div className="flex flex-col mt-8 mb-10">
        <div className="flex flex-row justify-between px-10">
          <div className="flex flex-col mr-14">
            <p className="mb-2 text-base font-medium text-gray-800">
              {capitalize(objectType.name)} enrichment settings
            </p>
            <p className="max-w-3xl text-sm text-gray-600">
              Activate Clearbit Enrichment and determine if you'd like to
              backfill historical records.
            </p>
          </div>
          <CKBox className="p-4">
            <div className="flex flex-row items-center justify-between pb-4 border-b border-gray-100">
              <div className="flex flex-col mr-20 w-96">
                <p className="mb-2 text-base font-medium text-gray-800">
                  Enrich {readableType(integration)} {objectType.name} fields
                </p>
                <p className="max-w-3xl text-sm text-gray-600">
                  When enabled, new {objectType.name}s in{' '}
                  {readableType(integration)} will be enriched with the data
                  you’ve mapped from Clearbit.
                </p>
              </div>
              {loading ? (
                <Spinner className="mx-4" />
              ) : (
                <CKSwitch
                  id="field mapping status"
                  checked={isActive}
                  onChange={() => setConfirmToggleDialogOpen(true)}>
                  {isActive ? 'Enabled' : 'Disabled'}
                </CKSwitch>
              )}
            </div>

            <div className="flex flex-row items-center justify-between pt-4">
              <div className="flex flex-col mr-20 w-96">
                <p className="mb-2 text-base font-medium text-gray-800">
                  Backfill {readableType(integration)} {objectType.name} fields
                </p>
                <p className="max-w-3xl text-sm text-gray-600">
                  Enrich all {objectType.name}s in {readableType(integration)}.
                  This process may take some time.
                </p>
              </div>
              {loading ? (
                <Spinner className="mx-4" />
              ) : accountConnectorBackfillActive ? (
                <CKButton
                  isLoading={backfillLoading}
                  variant={CKButton.Variant.simple}
                  variantColor={CKButton.VariantColor.red}
                  onClick={cancelBackfill}>
                  Cancel Backfill
                </CKButton>
              ) : (
                <StartBackfillButton
                  disabled={!isActive}
                  onClick={triggerBackfill}
                  isLoading={backfillLoading}
                />
              )}
            </div>
          </CKBox>
        </div>

        <div className="flex flex-row items-center pt-10 mx-10">
          <div className="flex flex-col">
            <p className="mb-2 text-base font-medium text-gray-800">
              {readableType(integration)} field mappings for enrichment
            </p>
            <p className="max-w-3xl text-sm text-gray-600">
              Select the fields in {readableType(integration)} {objectType.name}
              s that Clearbit will enrich, the source for that data, and your
              data overwrite preference.{' '}
              <a
                href="https://help.clearbit.com/hc/en-us/articles/4420681386135"
                className="text-blue-500 underline">
                Learn more
              </a>{' '}
              about Clearbit data enrichment.
            </p>
          </div>
        </div>
        <LoadingArea loading={loading}>
          {fieldMappings.length === 0 ? (
            <CKBox
              className="flex flex-col items-center p-10 m-10"
              variant={CKBox.Variant['tinted-frame']}>
              <EmptyState />
              <p className="text-base font-medium text-center text-gray-800 mt-7">
                Add your first mapping
              </p>
              <p className="max-w-md mt-2 text-sm text-center text-gray-600">
                There are no field mappings for enriching{' '}
                {readableType(integration)} {objectType.name}s, yet. Add your
                first mapping or address group to get started.
              </p>
              <div className="mt-6">
                <CKButton
                  onClick={() => addMapping()}
                  className="mr-4"
                  leftIcon={<AddHollow />}>
                  Add mapping
                </CKButton>
                <CKButton disabled leftIcon={<AddHollow />}>
                  Add address group
                </CKButton>
              </div>
            </CKBox>
          ) : (
            <div className="px-10">
              <div className="mt-6 mb-3 text-gray-600 mapping-row-header">
                <p className="text-base font-medium">Source</p>
                &nbsp;
                <p className="text-base font-medium">Target</p>
                <div className="flex items-center justify-start">
                  <p className="pl-4 pr-2 text-base font-medium">
                    Hard Overwrite
                  </p>
                  <CKTooltip
                    tooltip={
                      <p className="mr-2 text-base font-normal">
                        By default, Clearbit will never overwrite your manually
                        entered data. Enabling Hard Overwrite will ensure that
                        we force overwrite the field with new data regardless of
                        its previous value.
                      </p>
                    }
                    arrowPlacement="bottom"
                    options={{ placement: 'top-start' }}
                    animated={false}>
                    <div className="flex text-gray-300 outline-none">
                      <Unknown width="16" height="16" />
                    </div>
                  </CKTooltip>
                </div>
              </div>
              <div className="space-y-6">
                {sortedMappings.map((mapping) => {
                  if (mapping.groupId) {
                    return (
                      <FieldMappingGroup
                        key={`group:${mapping.groupId}`}
                        group={mapping}
                        type={integration}
                        objectType={objectType}
                        onChange={handleMappingChange}
                        onGroupChange={applyChangesToAllGroupMembers(
                          mapping.groupId,
                        )}
                        onRemove={removeMappingGroup(mapping.groupId)}
                        onRemoveMapping={removeMapping}
                        sourceAttributes={sourceAttributes}
                        targetAttributes={targetAttributes}
                        addMapping={addMapping}
                        showError={showInvalidFields}
                        errors={fieldErrors}
                      />
                    );
                  }

                  return (
                    <FieldMappingRow
                      index={mapping.index}
                      key={`${mapping.targetField || 'empty'}
                      :${mapping.sourceField || 'empty'}
                      -${mapping.index}`}
                      mapping={mapping}
                      type={integration}
                      objectType={objectType}
                      onChange={handleMappingChange(mapping.index)}
                      onClickRefreshAttributes={onClickRefreshAttributes}
                      onRemove={removeMapping(mapping.index)}
                      sourceAttributes={sourceAttributes}
                      targetAttributes={targetAttributes}
                      sourceErrors={fieldErrors[mapping.sourceField]}
                      targetErrors={fieldErrors[mapping.targetField]}
                      showError={showInvalidFields}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </LoadingArea>
      </div>

      <Footer
        left={
          <div>
            <CKButton
              onClick={() => addMapping()}
              className="mr-4"
              leftIcon={<AddHollow />}>
              Add mapping
            </CKButton>
            <CKButton
              onClick={() => addMapping({ groupId: uuidv4() })}
              leftIcon={<AddHollow />}>
              Add address group
            </CKButton>
          </div>
        }
        right={
          areFieldMappingsValid ? (
            <CKTooltip
              unstable_portal={false}
              tooltip={<p className="m-2">Some field mappings are invalid</p>}
              placement="top">
              <div
                onMouseEnter={() => setShowInvalidFields(true)}
                onMouseLeave={() => setShowInvalidFields(false)}>
                <SaveButton />
              </div>
            </CKTooltip>
          ) : (
            <SaveButton />
          )
        }
      />

      <CKModal
        baseId="toggle-field-mapping-modal"
        visible={confirmToggleDialogOpen}
        hide={() => setConfirmToggleDialogOpen(false)}
        size="md"
        aria-label="Modal">
        <CKModal.Header>
          <CKModal.Heading>
            Are you sure you want to {isActive ? 'disable' : 'enable'} enriching{' '}
            {readableType(integration)} {objectType.name}s?
          </CKModal.Heading>
        </CKModal.Header>
        <CKModal.Body className="px-10 py-6">
          {isActive
            ? `${readableType(integration)} ${
                objectType.name
              }s previously enriched by Clearbit data will
          retain that data.`
            : `${readableType(integration)} ${
                objectType.name
              }s will be enriched with Clearbit data.`}
        </CKModal.Body>
        <CKModal.Footer>
          <CKButton
            className="mr-2"
            onClick={() => setConfirmToggleDialogOpen(false)}>
            Cancel
          </CKButton>
          <CKButton
            isLoading={isToggling}
            onClick={async () => {
              await toggleFieldMapping();
              setConfirmToggleDialogOpen(false);
            }}
            variant={CKButton.Variant.bold}
            variantColor="red">
            Yes, {isActive ? 'disable' : 'enable'} syncing data to{' '}
            {objectType.name}s
          </CKButton>
        </CKModal.Footer>
      </CKModal>

      <CKModal
        baseId="toggle-field-mapping-modal"
        visible={confirmSaveDialogOpen}
        hide={() => setConfirmSaveDialogOpen(false)}
        size="md"
        aria-label="Modal">
        <CKModal.Header>
          <CKModal.Heading>
            {`Would you like to backfill all ${objectType.name}s with this mapping?`}
          </CKModal.Heading>
        </CKModal.Header>
        <CKModal.Body className="px-10 py-6">
          <p>
            {`Without a backfill, you may see inconsistencies in reporting and
              data synced to ${objectType.name}s in ${readableType(
              integration,
            )}`}
          </p>
        </CKModal.Body>
        <CKModal.Footer>
          <CKButton
            className="mr-2"
            isLoading={triggering || isUpdating}
            onClick={async () => {
              await updateFieldMapping();
              setConfirmSaveDialogOpen(false);
            }}>
            No, thanks
          </CKButton>
          <CKButton
            variant={CKButton.Variant.bold}
            variantColor="blue"
            isLoading={triggering || isUpdating}
            onClick={async () => {
              await updateFieldMapping({
                variables: { input: { ...input, backfill: true } },
              });
              setConfirmSaveDialogOpen(false);
            }}>
            Start backfill
          </CKButton>
        </CKModal.Footer>
      </CKModal>
    </div>
  );
};

export default FieldMappingForm;
